import { useDeferredValue, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Search, X } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { MultiSelect, type MultiSelectOption } from '@/components/ui/multi-select'
import { cn } from '@/lib/utils'
import { AdUnitsApi, type AdUnit } from '@/services/adUnits/adUnitsApi'
import { EarningsAccountsApi } from '@/services/earnings/earningsAccountsApi'
import { SITE_LIST_LIMIT, SitesApi, type Site } from '@/services/sites/sitesApi'
import type { ObjectRef, Targeting } from '@/services/targeting/targeting'
import { COUNTRIES } from '../../data/countries'
import { FieldLabel } from '../FieldLabel'
import { PickerColumn, type PickerColumnRow } from '../PickerColumn'
import { NegateToggle } from './NegateToggle'

/** The four tiers this editor browses, widest first. */
export type InventoryTier = 'siteCountry' | 'publisher' | 'site' | 'adUnit'

export const INVENTORY_TIERS: InventoryTier[] = ['siteCountry', 'publisher', 'site', 'adUnit']
/* Only include active inventory */
const ACTIVE = 'ACTIVE' as const

/** The tiers that can be excluded — the API has no negated site-country target. */
export type NegatableTier = Exclude<InventoryTier, 'siteCountry'>

/** The four inventory targets, pulled out of a targeting and put back into it. */
export interface InventoryTargetValue {
  countries: string[]
  publishers: ObjectRef[]
  sites: ObjectRef[]
  adUnits: ObjectRef[]
  /** Per tier: its selection is excluded rather than targeted. */
  negated: Record<NegatableTier, boolean>
}

/** Reads the inventory selection out of a targeting object. */
export function readInventory(targeting: Targeting): InventoryTargetValue {
  return {
    countries: targeting.siteCountryTarget?.countries ?? [],
    publishers: targeting.publisherTarget?.earningsAccounts ?? [],
    sites: targeting.siteTarget?.sites ?? [],
    adUnits: targeting.adUnitTarget?.adUnits ?? [],
    negated: {
      publisher: !!targeting.publisherTarget?.negated,
      site: !!targeting.siteTarget?.negated,
      adUnit: !!targeting.adUnitTarget?.negated,
    },
  }
}

/** Writes an inventory selection back onto a targeting object. */
export function writeInventory(targeting: Targeting, value: InventoryTargetValue): Targeting {
  return {
    ...targeting,
    siteCountryTarget: { ...targeting.siteCountryTarget, countries: value.countries },
    publisherTarget: {
      ...targeting.publisherTarget,
      earningsAccounts: value.publishers,
      negated: value.negated.publisher,
    },
    siteTarget: { ...targeting.siteTarget, sites: value.sites, negated: value.negated.site },
    adUnitTarget: {
      ...targeting.adUnitTarget,
      adUnits: value.adUnits,
      negated: value.negated.adUnit,
    },
  }
}

/** How many things the inventory targets, all four tiers together. */
export function countInventory(value: InventoryTargetValue): number {
  return (
    value.countries.length + value.publishers.length + value.sites.length + value.adUnits.length
  )
}

/** The same selection with nothing picked; the include/exclude switches are kept. */
export function clearInventory(value: InventoryTargetValue): InventoryTargetValue {
  return { ...value, countries: [], publishers: [], sites: [], adUnits: [] }
}

/**
 * Whether the inventory only excludes: something is picked, and every tier
 * holding a pick is excluded. A mix of targeted and excluded tiers is not.
 */
export function isInventoryNegated(value: InventoryTargetValue): boolean {
  const used: [NegatableTier, number][] = [
    ['publisher', value.publishers.length],
    ['site', value.sites.length],
    ['adUnit', value.adUnits.length],
  ]
  const picked = used.filter(([, count]) => count > 0)
  return (
    !value.countries.length && picked.length > 0 && picked.every(([tier]) => value.negated[tier])
  )
}

/**
 * The ids a tier narrows the tiers to its right by. An excluded tier says
 * nothing about what is worth showing next to it, so it narrows nothing.
 */
const narrowingIds = (refs: ObjectRef[], negated: boolean) =>
  negated ? [] : refs.map((ref) => ref.id)

/**
 * The ids a tier takes out of the tiers to its right: what it excludes. The
 * sites of an excluded publisher, and the ad units of an excluded site, can
 * never deliver, so they are not offered.
 */
const excludedIds = (refs: ObjectRef[], negated: boolean) =>
  negated ? refs.map((ref) => ref.id) : []

/**
 * The rows with every pick they lack put first, by the name the pick carries.
 * A pick outside what is loaded — an ad unit past the first page, say — must
 * still be shown, so it can be seen and unticked.
 */
const withPicked = (rows: PickerColumnRow[], refs: ObjectRef[]): PickerColumnRow[] => {
  const ids = new Set(rows.map((row) => row.id))
  const missing = refs
    .filter((ref) => !ids.has(ref.id))
    .map((ref) => ({ id: ref.id, name: ref.name || ref.id }))
  return missing.length ? [...missing, ...rows] : rows
}

/** The most rows the API's list endpoints return in one page. */
const API_PAGE_SIZE = 100

const NO_AD_UNITS: AdUnit[] = []

/** The query holding every active site, before any country or publisher is picked. */
const ALL_SITES_KEY = ['targeting-inventory-sites', 'all', '']

const CHIP_VARIANTS: Record<InventoryTier, 'secondary' | 'blue' | 'warning' | 'success'> = {
  siteCountry: 'secondary',
  publisher: 'blue',
  site: 'warning',
  adUnit: 'success',
}

const countryName = (code: string) =>
  COUNTRIES.find((country) => country.value === code)?.label ?? code

const dimensions = (adUnit: AdUnit) =>
  adUnit.dimensions.map(([width, height]) => `${width}×${height}`).join(', ')

export interface InventoryTargetEditorProps {
  value: InventoryTargetValue
  onChange: (value: InventoryTargetValue) => void
  /** The tiers the line item is allowed to target; the rest are left out. */
  tiers?: InventoryTier[]
}

/**
 * Browses the inventory as four cascading tiers — site country, publisher, site,
 * ad unit — where what is picked in one tier is all that the tiers to its right
 * offer. Every checked row goes into its own target. Site countries are picked
 * in a field above the browser; the other three are its columns.
 *
 * Publishers, sites and ad units each switch between including and excluding
 * what is checked. An excluded tier is still narrowed by the included tiers to
 * its left, and takes what it excludes out of the tiers to its right: an
 * excluded publisher's sites and ad units are not offered, as they could never
 * deliver. Its picks are never dropped by a change to the left, though: an
 * exclusion does no harm out of scope, and comes back into play when the
 * scope widens again. Site countries can only be included, as the API has no
 * way to exclude one.
 */
export function InventoryTargetEditor({
  value,
  onChange,
  tiers = INVENTORY_TIERS,
}: InventoryTargetEditorProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')

  const shown = (tier: InventoryTier) => tiers.includes(tier)

  const { data: publishers = [], isLoading: publishersLoading } = useQuery({
    queryKey: ['targeting-inventory-publishers'],
    queryFn: () => EarningsAccountsApi.getAll(undefined, ACTIVE),
  })
  // The country list comes from the network's own site-country summary rather
  // than from the loaded sites, so every country is offered even though only a
  // slice of the inventory is ever held in the browser.
  const { data: siteCountries = [] } = useQuery({
    queryKey: ['targeting-inventory-site-countries'],
    enabled: shown('siteCountry'),
    queryFn: () => SitesApi.getCountries(),
  })

  // The columns can hold thousands of rows, and widening the scope — removing a
  // country, say — renders all of them. So they follow a deferred copy of the
  // selection: the chips and the country field answer the click at once, and
  // the columns catch up behind them.
  const deferred = useDeferredValue(value)
  const columnsStale = deferred !== value

  const selectedCountries = deferred.countries
  const selectedPublisherIds = useMemo(
    () => deferred.publishers.map((ref) => ref.id),
    [deferred.publishers]
  )
  const selectedSiteIds = useMemo(() => deferred.sites.map((ref) => ref.id), [deferred.sites])
  const selectedAdUnitIds = useMemo(() => deferred.adUnits.map((ref) => ref.id), [deferred.adUnits])
  // What each object tier narrows the tiers to its right by, and takes out of them.
  const narrowPublisherIds = narrowingIds(deferred.publishers, deferred.negated.publisher)
  const narrowSiteIds = narrowingIds(deferred.sites, deferred.negated.site)
  const excludedPublisherIds = excludedIds(deferred.publishers, deferred.negated.publisher)
  const excludedSiteIds = excludedIds(deferred.sites, deferred.negated.site)

  // Sites are fetched for whatever is targeted above them: a network holds far
  // more sites than a browser wants, so only the picked scope is loaded.
  const siteScope = useMemo(() => {
    if (selectedCountries.length) return { kind: 'countries' as const, ids: selectedCountries }
    if (narrowPublisherIds.length) return { kind: 'publishers' as const, ids: narrowPublisherIds }
    return { kind: 'all' as const, ids: [] as string[] }
  }, [narrowPublisherIds.join(','), selectedCountries.join(',')])

  const { data: sites = [], isLoading: sitesLoading } = useQuery({
    queryKey: ['targeting-inventory-sites', siteScope.kind, siteScope.ids.join(',')],
    queryFn: async () => {
      if (siteScope.kind === 'all') return SitesApi.getAll(undefined, undefined, ACTIVE)
      // Every active site is already held, and the rows narrow it by country
      // and publisher themselves, so a narrower scope has nothing to fetch. A
      // list cut short at the limit may be missing some, so it is not trusted.
      const all = queryClient.getQueryData<Site[]>(ALL_SITES_KEY)
      if (all && all.length < SITE_LIST_LIMIT) return all
      if (siteScope.kind === 'countries')
        return SitesApi.getByCountries(siteScope.ids, undefined, ACTIVE)
      const pages = await Promise.all(
        siteScope.ids.map((id) => SitesApi.getByEarningsAccount(id, undefined, ACTIVE))
      )
      const byId = new Map<string, Site>()
      for (const site of pages.flat()) byId.set(site.id, site)
      return [...byId.values()]
    },
  })

  // Ad units are the one tier too numerous to hold in full, so they are fetched
  // for whatever is targeted above them.
  const adUnitScope = useMemo(() => {
    if (narrowSiteIds.length) return { kind: 'sites' as const, ids: narrowSiteIds }
    if (narrowPublisherIds.length) return { kind: 'publishers' as const, ids: narrowPublisherIds }
    return { kind: 'all' as const, ids: [] as string[] }
  }, [narrowSiteIds.join(','), narrowPublisherIds.join(',')])

  const { data: adUnitData, isLoading: adUnitsLoading } = useQuery({
    queryKey: ['targeting-inventory-adunits', adUnitScope.kind, adUnitScope.ids.join(',')],
    enabled: shown('adUnit'),
    queryFn: async () => {
      const fetchOne =
        adUnitScope.kind === 'sites' ? AdUnitsApi.getBySite : AdUnitsApi.getByEarningsAccount
      const pages =
        adUnitScope.kind === 'all'
          ? [await AdUnitsApi.getAll(undefined, ACTIVE)]
          : await Promise.all(adUnitScope.ids.map((id) => fetchOne(id, undefined, ACTIVE)))
      const byId = new Map<string, AdUnit>()
      for (const adUnit of pages.flat()) byId.set(adUnit.id, adUnit)
      return {
        adUnits: [...byId.values()],
        // Only the first page of each scope is read, and a full one may have
        // more behind it, so the units listed cannot be called all of them.
        complete: pages.every((page) => page.length < API_PAGE_SIZE),
      }
    },
  })
  const adUnits = adUnitData?.adUnits ?? NO_AD_UNITS

  const siteById = useMemo(() => new Map(sites.map((site) => [site.id, site])), [sites])

  /** The publisher an ad unit belongs to, through its site when the unit does not say. */
  const publisherOf = (adUnit: AdUnit) =>
    (adUnit.site && siteById.get(adUnit.site.id)?.earningsAccountId) || adUnit.earningsAccountId

  // Which publishers and sites each tier leaves standing.
  const inCountry = (country: string) =>
    !selectedCountries.length || selectedCountries.includes(country)

  // A publisher's own `countries` is the set of countries its sites are in, as
  // computed by the API, so the country tier narrows publishers without the
  // sites themselves having to be loaded.
  const publisherRows: PickerColumnRow[] = useMemo(
    () =>
      withPicked(
        publishers
          .filter(
            (publisher) =>
              !selectedCountries.length ||
              publisher.countries.some((code) => selectedCountries.includes(code))
          )
          .map((publisher) => ({
            id: publisher.id,
            name: publisher.name,
            meta: publisher.countries.join(', '),
          })),
        deferred.publishers
      ),
    [publishers, selectedCountries.join(','), deferred.publishers]
  )

  const countryOptions: MultiSelectOption[] = useMemo(() => {
    const codes = new Set(siteCountries.map((entry) => entry.country))
    // A targeted country stays listed even when it no longer holds sites.
    for (const code of value.countries) codes.add(code)
    return [...codes]
      .map((code) => ({ value: code, label: countryName(code) }))
      .sort((a, b) => a.label.localeCompare(b.label))
  }, [siteCountries, value.countries.join(',')])

  const siteRows: PickerColumnRow[] = useMemo(
    () =>
      withPicked(
        sites
          .filter(
            (site) =>
              inCountry(site.country) &&
              (!narrowPublisherIds.length || narrowPublisherIds.includes(site.earningsAccountId)) &&
              !excludedPublisherIds.includes(site.earningsAccountId)
          )
          .map((site) => ({
            id: site.id,
            name: site.name,
            sub: site.earningsAccountName,
            meta: site.country,
          })),
        deferred.sites
      ),
    [
      sites,
      deferred.sites,
      selectedCountries.join(','),
      narrowPublisherIds.join(','),
      excludedPublisherIds.join(','),
    ]
  )

  const adUnitRows: PickerColumnRow[] = useMemo(() => {
    // The fetch is scoped by publisher or site, but not by country, and takes
    // nothing excluded out, so those cuts are made here.
    const allowed = new Set(siteRows.map((row) => row.id))
    const narrowing =
      selectedCountries.length > 0 || narrowPublisherIds.length > 0 || narrowSiteIds.length > 0
    const rows = adUnits
      .filter(
        (adUnit) =>
          !(adUnit.site && excludedSiteIds.includes(adUnit.site.id)) &&
          !excludedPublisherIds.includes(publisherOf(adUnit)) &&
          (!narrowing || (adUnit.site && allowed.has(adUnit.site.id)))
      )
      .map((adUnit) => ({
        id: adUnit.id,
        name: adUnit.name,
        // The sizes say more about a unit than its site does — and the site is
        // already the column to the left.
        sub: dimensions(adUnit),
      }))
    return withPicked(rows, deferred.adUnits)
  }, [
    adUnits,
    deferred.adUnits,
    siteRows,
    siteById,
    selectedCountries.join(','),
    narrowPublisherIds.join(','),
    narrowSiteIds.join(','),
    excludedPublisherIds.join(','),
    excludedSiteIds.join(','),
  ])

  /**
   * Applies a change and drops every included pick it puts out of scope, so
   * what is targeted always matches what the columns are showing. Excluded
   * picks are kept whatever the tiers to their left say, and prune what they
   * exclude from the tiers to their right.
   */
  const apply = (next: InventoryTargetValue) => {
    const countries = next.countries
    const keepSite = (id: string) => {
      const site = siteById.get(id)
      // A site the list does not hold cannot be judged, so it is kept.
      if (!site) return true
      if (countries.length && !countries.includes(site.country)) return false
      return true
    }
    const publisherCountries = new Map(publishers.map((p) => [p.id, p.countries]))
    const keptPublishers =
      countries.length && !next.negated.publisher
        ? next.publishers.filter((ref) => {
            const owned = publisherCountries.get(ref.id)
            // A publisher the list does not hold cannot be judged, so it is kept.
            if (!owned) return true
            return owned.some((code) => countries.includes(code))
          })
        : next.publishers
    const publisherIds = narrowingIds(keptPublishers, next.negated.publisher)
    const outPublisherIds = excludedIds(keptPublishers, next.negated.publisher)
    const sitesKept = next.negated.site
      ? next.sites
      : next.sites.filter((ref) => {
          if (!keepSite(ref.id)) return false
          const site = siteById.get(ref.id)
          if (publisherIds.length && site && !publisherIds.includes(site.earningsAccountId))
            return false
          if (site && outPublisherIds.includes(site.earningsAccountId)) return false
          return true
        })
    const siteIds = narrowingIds(sitesKept, next.negated.site)
    const outSiteIds = excludedIds(sitesKept, next.negated.site)
    const adUnitById = new Map(adUnits.map((adUnit) => [adUnit.id, adUnit]))
    const adUnitsKept = next.negated.adUnit
      ? next.adUnits
      : next.adUnits.filter((ref) => {
          const adUnit = adUnitById.get(ref.id)
          // Same as above: an ad unit outside the loaded page is left alone.
          if (!adUnit?.site) return true
          if (outSiteIds.includes(adUnit.site.id)) return false
          if (outPublisherIds.includes(publisherOf(adUnit))) return false
          if (siteIds.length) return siteIds.includes(adUnit.site.id)
          if (!keepSite(adUnit.site.id)) return false
          const site = siteById.get(adUnit.site.id)
          if (publisherIds.length && site && !publisherIds.includes(site.earningsAccountId))
            return false
          return true
        })

    onChange({ ...next, publishers: keptPublishers, sites: sitesKept, adUnits: adUnitsKept })
  }

  // The columns are memoized on the deferred selection, so what they call back
  // into reads the current one from here.
  const latest = useRef({ value, apply })
  useLayoutEffect(() => {
    latest.current = { value, apply }
  })

  /** Toggles one row of an object tier, keeping the name for the chips. */
  const toggleRef =
    (key: 'publishers' | 'sites' | 'adUnits', rows: PickerColumnRow[]) =>
    (id: string, selected: boolean) => {
      const { value, apply } = latest.current
      const current = value[key]
      const next = selected
        ? [...current, { id, name: rows.find((row) => row.id === id)?.name ?? id }]
        : current.filter((ref) => ref.id !== id)
      apply({ ...value, [key]: next })
    }

  const chips: {
    tier: InventoryTier
    id: string
    label: string
    negated?: boolean
    remove: () => void
  }[] = [
    ...value.countries.map((code) => ({
      tier: 'siteCountry' as const,
      id: code,
      label: countryName(code),
      remove: () => apply({ ...value, countries: value.countries.filter((c) => c !== code) }),
    })),
    ...value.publishers.map((ref) => ({
      tier: 'publisher' as const,
      id: ref.id,
      label: ref.name || ref.id,
      negated: value.negated.publisher,
      remove: () =>
        apply({ ...value, publishers: value.publishers.filter((p) => p.id !== ref.id) }),
    })),
    ...value.sites.map((ref) => ({
      tier: 'site' as const,
      id: ref.id,
      label: ref.name || ref.id,
      negated: value.negated.site,
      remove: () => apply({ ...value, sites: value.sites.filter((s) => s.id !== ref.id) }),
    })),
    ...value.adUnits.map((ref) => ({
      tier: 'adUnit' as const,
      id: ref.id,
      label: ref.name || ref.id,
      negated: value.negated.adUnit,
      remove: () => apply({ ...value, adUnits: value.adUnits.filter((a) => a.id !== ref.id) }),
    })),
  ]

  /** The include/exclude switch heading an object tier's column. */
  const negateToggle = (tier: NegatableTier) => (
    <NegateToggle
      compact={true}
      negated={deferred.negated[tier]}
      what={t(`targeting.types.${tier}.plural`).toLowerCase()}
      onChange={(negated) => {
        const { value, apply } = latest.current
        apply({ ...value, negated: { ...value.negated, [tier]: negated } })
      }}
    />
  )

  // Memoized so that a click elsewhere — a chip, the country field — re-renders
  // none of the rows until the deferred selection catches up.
  const browser = useMemo(() => {
    const columns: { tier: InventoryTier; node: React.ReactNode; wide?: boolean }[] = [
      {
        tier: 'publisher',
        node: (
          <PickerColumn
            label={t('targeting.types.publisher.plural')}
            rows={publisherRows}
            toolbar={negateToggle('publisher')}
            negated={deferred.negated.publisher}
            what={t('targeting.types.publisher.plural').toLowerCase()}
            selectedIds={selectedPublisherIds}
            showSelectedToggle
            onToggle={toggleRef('publishers', publisherRows)}
            searchPlaceholder={t('targeting.searchType', {
              what: t('targeting.types.publisher.plural').toLowerCase(),
            })}
            filter={search}
            loading={publishersLoading}
            emptyText={t('targeting.inventory.empty')}
          />
        ),
      },
      {
        tier: 'site',
        node: (
          <PickerColumn
            label={t('targeting.types.site.plural')}
            rows={siteRows}
            toolbar={negateToggle('site')}
            negated={deferred.negated.site}
            what={t('targeting.types.site.plural').toLowerCase()}
            selectedIds={selectedSiteIds}
            showSelectedToggle
            onToggle={toggleRef('sites', siteRows)}
            searchPlaceholder={t('targeting.searchType', {
              what: t('targeting.types.site.plural').toLowerCase(),
            })}
            filter={search}
            loading={sitesLoading}
            emptyText={t('targeting.inventory.empty')}
          />
        ),
      },
      {
        tier: 'adUnit',
        // Unit names are the longest in the browser, and carry their sizes under
        // them, so this column gets the extra room.
        wide: true,
        node: (
          <PickerColumn
            label={t('targeting.types.adUnit.plural')}
            rows={adUnitRows}
            toolbar={negateToggle('adUnit')}
            negated={deferred.negated.adUnit}
            what={t('targeting.types.adUnit.plural').toLowerCase()}
            complete={adUnitData?.complete}
            selectedIds={selectedAdUnitIds}
            showSelectedToggle
            onToggle={toggleRef('adUnits', adUnitRows)}
            searchPlaceholder={t('targeting.searchType', {
              what: t('targeting.types.adUnit.plural').toLowerCase(),
            })}
            filter={search}
            loading={adUnitsLoading}
            emptyText={t('targeting.inventory.empty')}
          />
        ),
      },
    ]

    return (
      // Fixed column widths — a long name must not push its column wider than
      // the rest. Narrow screens scroll. Dimmed while catching up with a change.
      <div
        className={cn(
          'flex h-96 overflow-x-auto rounded-md border transition-opacity',
          columnsStale && 'opacity-60'
        )}
      >
        {columns
          .filter((column) => shown(column.tier))
          .map((column) => (
            <div
              key={column.tier}
              className={cn(
                'flex h-full shrink-0 grow flex-col border-r last:border-r-0',
                column.wide ? 'w-96 basis-96' : 'w-70 basis-70'
              )}
            >
              {column.node}
            </div>
          ))}
      </div>
    )
  }, [
    t,
    tiers.join(','),
    search,
    columnsStale,
    deferred.negated,
    publisherRows,
    siteRows,
    adUnitRows,
    selectedPublisherIds,
    selectedSiteIds,
    selectedAdUnitIds,
    publishersLoading,
    sitesLoading,
    adUnitsLoading,
    adUnitData?.complete,
  ])

  return (
    <div className="flex flex-col gap-4">
      {/* Site countries sit above the browser rather than in it: they can only
          be targeted, and a line item seldom holds more than one, so a column
          of its own would be mostly empty. */}
      {shown('siteCountry') && (
        <div className="flex flex-col gap-1.5">
          <FieldLabel
            className="text-xs font-medium"
            tooltip={t('targeting.types.siteCountry.help')}
          >
            {t('targeting.types.siteCountry.plural')}
          </FieldLabel>
          <MultiSelect
            options={countryOptions}
            value={value.countries}
            onChange={(countries) => apply({ ...value, countries })}
            placeholder={t('targeting.inventory.anyCountry')}
          />
        </div>
      )}

      <div className="relative">
        <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t('targeting.inventory.searchAll')}
          className="pl-9"
        />
      </div>

      {browser}

      <div className="flex flex-col gap-2 rounded-md border border-(--ad-primary-subtle) p-3">
        <span className="text-[11px] font-semibold tracking-wider text-muted-foreground uppercase">
          {t('targeting.selected')}
        </span>
        {chips.length === 0 && (
          <p className="text-xs text-muted-foreground">
            {t('targeting.inventory.nothingSelected')}
          </p>
        )}
        <div className="flex flex-wrap gap-1.5">
          {chips.map((chip) => (
            <Badge
              key={`${chip.tier}-${chip.id}`}
              variant={CHIP_VARIANTS[chip.tier]}
              className="gap-1.5"
            >
              <span className="text-[10px] tracking-wider uppercase opacity-70">
                {t(`targeting.inventory.tier.${chip.tier}`)}
                {chip.negated && ` · ${t('targeting.excluded')}`}
              </span>
              <span className={cn(chip.negated && 'line-through')}>{chip.label}</span>
              <button
                type="button"
                onClick={chip.remove}
                aria-label={t('common.remove')}
                className="opacity-60 hover:opacity-100"
              >
                <X className="size-3" />
              </button>
            </Badge>
          ))}
        </div>
      </div>
    </div>
  )
}
